import useSWRImmutable from 'swr/immutable';

import { fetchEndpointData, getEndpointUrl, useEndpoint } from 'api';
import { useSelectedStore } from 'contexts';
import { getDpApiEndpointUrl } from 'endpoints/dpApi';
import { usePath } from 'hooks/navigation';
import type {
	CampaignProductsResponse,
	RelatedProductsResponse,
	RelationType,
	ReviewResponse,
	ReviewScore,
	ReviewSorting,
	SimilarProductsResponse,
} from 'models/api';
import type { ProductId, VariantId } from 'models/product';
import type { CampaignId, JSONObject, StoreId } from 'types';
import { empty } from 'utils/helpers';
import { parseJson } from 'utils/string';

import usePagedData from './usePagedData';

export function usePopularProducts({
	categoryId,
	isActive = true,
	itemLimit,
}: {
	categoryId?: string;
	isActive?: boolean;
	itemLimit?: number;
}) {
	return useEndpoint(
		{
			name: 'GetSearchPopularProducts',
			queryParams: {
				pageSize: itemLimit ?? 5,
				categoryId,
			},
		},
		{ isActive },
	);
}

export function usePopularSearchTerms({
	isActive = true,
	itemLimit,
}: { isActive?: boolean; itemLimit?: number } = {}) {
	const { data, error, isLoading } = useEndpoint('GetSearchPopularTerms', {
		isActive,
	});

	return {
		// Don't want zero.
		// oxlint-disable-next-line typescript/prefer-nullish-coalescing
		terms: data ? data.slice(0, itemLimit || 999) : empty.array,
		isLoading,
		error,
	};
}

export function useFaqSearch(
	query: string,
	{
		includeContent = false,
		limit,
	}: { includeContent?: boolean; limit?: number } = {},
) {
	return useEndpoint(
		query
			? {
					name: 'GetFaqSearch',
					pathParams: { searchString: query },
					queryParams: {
						pageSize: limit,
						includeContent: includeContent ? 'true' : 'false',
					},
				}
			: null,
	);
}

export function useReviews({
	filter,
	isActive,
	pageSize,
	reviewScore,
	reviewSorting,
	variantId,
}: {
	filter?: 'HasImage';
	isActive: boolean;
	pageSize?: number;
	reviewScore: ReviewScore | undefined;
	reviewSorting: ReviewSorting | undefined;
	variantId: VariantId;
}) {
	return usePagedData<ReviewResponse, 'reviews'>({
		baseUrl: isActive
			? getDpApiEndpointUrl({
					name: 'GetReviews',
					pathParams: { variantId },
					queryParams: {
						filter,
						reviewScore,
						reviewSorting,
					},
				})
			: null,
		itemsKey: 'reviews',
		// To enable differentiating between initial loading and
		// loading because of filtering or sorting changes.
		keepPreviousData: true,
		pageSize,
	});
}

export function useRelatedProducts({
	isActive,
	pageSize = 15,
	relationType,
	returnStockData,
	storeId,
	variantId,
}: {
	isActive: boolean;
	pageSize?: number;
	relationType: RelationType;
	returnStockData?: boolean;
	storeId?: StoreId;
	variantId: VariantId | undefined;
}) {
	return usePagedData<RelatedProductsResponse, 'variants'>({
		baseUrl: variantId
			? getEndpointUrl({
					name: 'GetRelatedProducts',
					pathParams: { variantId },
					queryParams: {
						relationType,
						storeId,
						returnStockData: returnStockData ? 'true' : undefined,
					},
				})
			: null,

		itemsKey: 'variants',
		isActive,
		pageSize,
	});
}

export function useSimilarProducts({
	isActive,
	pageSize = 15,
	productId,
}: {
	isActive: boolean;
	pageSize?: number;
	productId: ProductId;
}) {
	return usePagedData<SimilarProductsResponse, 'products'>({
		baseUrl: getEndpointUrl({
			name: 'GetSimilarProducts',
			pathParams: {
				productId,
			},
		}),
		itemsKey: 'products',
		isActive,
		pageSize,
	});
}

export function useCampaignProducts(
	campaignId: CampaignId | undefined,
	isActive: boolean,
) {
	const { selectedStore } = useSelectedStore();
	return usePagedData<CampaignProductsResponse, 'variants'>({
		baseUrl: campaignId
			? getEndpointUrl({
					name: 'GetCampaignProducts',
					pathParams: { campaignId },
					queryParams: { storeId: selectedStore?.id },
				})
			: null,
		itemsKey: 'variants',
		isActive,
		pageSize: 15,
	});
}

export function useProductList(
	requestBody: JSONObject | undefined,
	isActive: boolean = true,
) {
	const keyPrefix = 'useProductList/';
	return useSWRImmutable(
		isActive && requestBody
			? `${keyPrefix}${JSON.stringify(requestBody)}`
			: null,
		(key) => {
			const body = parseJson<typeof requestBody>(key.replace(keyPrefix, ''));
			if (!body) {
				return undefined;
			}
			return fetchEndpointData({ name: 'PostProductList', body });
		},
	);
}

export function useSharedWishlist(isActive: boolean = true) {
	const { lastSegment: wishlistId } = usePath();

	const { selectedStore } = useSelectedStore();
	const { data, error, isLoading } = useEndpoint(
		wishlistId
			? {
					name: 'GetSharedWishlist',
					pathParams: { wishlistId },
					queryParams: {
						storeId: selectedStore?.id,
					},
				}
			: null,
		{ isActive },
	);

	return { isLoading, error, wishlist: data };
}
